import { AIConversationType } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	invalidateAccountProfileQueries,
	useAccountProfileQuery,
} from '../../../../queries/AccountProfile/useAccountProfileQuery';
import { invalidateAllAIConversationFilterQueries } from '../../../../queries/AIConversation/useAIConversationFilterQuery';
import { BusinessInfo, parseBusinessDescription } from '../../../../utils/businessInfoParser';
import { AIAssistantDialog } from '../../../components-v2/ai/AIAssistantDialog';
import { Button } from '../../../components-v2/Button';
import { LoadingSpinner } from '../../LoadingSpinner';
import { baseStyleSheet } from '../../../styles/styles';
import { aiCustomContentStyles, styleSheet } from './styles';

/**
 * Simple info field component with label over value
 */
const InfoField: React.FC<{ label: string; value: string | undefined }> = ({ label, value }) => {
	return (
		<div className={css(aiCustomContentStyles.infoFieldContainer)}>
			<div className={css(styleSheet.formFieldLabel, baseStyleSheet.mb1)}>{label}</div>
			<div className={css(styleSheet.formFieldValue, aiCustomContentStyles.infoFieldValue)}>{value || '-'}</div>
		</div>
	);
};

/**
 * Component to display structured business information
 */
const BusinessInfoDisplay: React.FC<{ businessInfo: BusinessInfo | null; keywords?: string }> = ({
	businessInfo,
	keywords,
}) => {
	return (
		<div className={css(aiCustomContentStyles.businessInfoGrid)}>
			{/* First row: 5 basic fields */}
			<InfoField label='Location' value={businessInfo?.location || '-'} />
			<InfoField label='Website' value={businessInfo?.website || '-'} />
			<InfoField label='Phone number' value={businessInfo?.phoneNumber || '-'} />
			<InfoField label='Pronouns' value={businessInfo?.pronouns || '-'} />
			<InfoField label='Tone preference' value={businessInfo?.tonePreference || '-'} />

			{/* Second row: Description (3 cols) and Keywords (2 cols) */}
			<div className={css(aiCustomContentStyles.columnSpan1to4)}>
				<div className={css(styleSheet.formFieldLabel, baseStyleSheet.mb1)}>Description</div>
				<div className={css(styleSheet.formFieldValue, aiCustomContentStyles.descriptionValue)}>
					{businessInfo?.description || '-'}
				</div>
			</div>
			<div className={css(aiCustomContentStyles.columnSpan4to6)}>
				<div className={css(styleSheet.formFieldLabel, baseStyleSheet.mb1)}>Keywords</div>
				<div className={css(styleSheet.formFieldValue)}>{keywords || '-'}</div>
			</div>
		</div>
	);
};

export const AICustomContentSection: React.FC = () => {
	const [aiAssistantOpen, setAiAssistantOpen] = React.useState(false);
	const userSession = useUserSession();
	const accountProfileQuery = useAccountProfileQuery({
		impersonationContext: userSession?.impersonationContext,
	});

	const initialMessage = React.useMemo(() => {
		const userName = userSession?.user?.firstName || 'there';
		const accountName = userSession?.account?.companyName || 'your account';
		return `Hi ${userName}! I'm here to help configure your Custom Content Preferences for "${accountName}."<br /><br />Give me a second while I check your current settings.`;
	}, [userSession?.user?.firstName, userSession?.account?.companyName]);

	const handleOpenChange = React.useCallback(
		(open: boolean) => {
			setAiAssistantOpen(open);
			if (!open) {
				// When dialog closes, invalidate the query to refetch the latest data
				invalidateAccountProfileQueries({ impersonationContext: userSession?.impersonationContext });
			} else {
				// When dialog opens, invalidate conversation filter queries to clear stale conversation cache
				invalidateAllAIConversationFilterQueries();
			}
		},
		[userSession?.impersonationContext]
	);

	if (accountProfileQuery.isPending) {
		return (
			<div className='settings-admin-company-container'>
				<div className={css(baseStyleSheet.mb2)}>
					<LoadingSpinner type='small' />
				</div>
			</div>
		);
	}

	if (accountProfileQuery.isError) {
		return (
			<div className='settings-admin-company-container'>
				<div className={css(baseStyleSheet.mb2)}>
					<p className={css(styleSheet.formFieldValue, aiCustomContentStyles.notConfiguredText)}>
						Error loading preferences. Please try again.
					</p>
				</div>
			</div>
		);
	}

	const businessDescription = accountProfileQuery.data?.businessDescription;
	const keywords = accountProfileQuery.data?.messagingPreferences?.keywords;

	// Try to parse the business description into structured format
	const parsedBusinessInfo = businessDescription ? parseBusinessDescription(businessDescription) : null;

	// Contextual button text based on whether data exists
	const hasAnyData = parsedBusinessInfo || keywords;
	const buttonText = hasAnyData ? 'Edit with AI Assistant' : 'Configure with AI Assistant';

	return (
		<div className='settings-admin-company-container'>
			<div className={css(baseStyleSheet.mb2)}>
				{hasAnyData ? (
					<BusinessInfoDisplay businessInfo={parsedBusinessInfo} keywords={keywords} />
				) : (
					<p className={css(styleSheet.formFieldValue, aiCustomContentStyles.notConfiguredText)}>Not configured</p>
				)}
			</div>

			<div className={css(baseStyleSheet.mb2, aiCustomContentStyles.buttonContainer)}>
				<Button variant='secondary' size='sm' onClick={() => setAiAssistantOpen(true)}>
					{buttonText}
				</Button>
			</div>

			<AIAssistantDialog
				open={aiAssistantOpen}
				onOpenChange={handleOpenChange}
				assistantName='AccountOnboardAgent'
				conversationType={AIConversationType.AccountOnboarding}
				title='Custom Content Preferences'
				initialMessage={initialMessage}
				placeholder='Send a message...'
				impersonationContext={userSession?.impersonationContext}
			/>
		</div>
	);
};
